import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, Loader2 } from 'lucide-react';
import { authService, setPendingVerificationContext } from '../services/auth.service';
import GoogleAuthButton from '../components/auth/GoogleAuthButton';
import AuthLayout from '../components/auth/AuthLayout';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roleFromQuery = searchParams.get('role') || 'RENTER';

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    identificationType: '',
    role: roleFromQuery
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(''); // Clear error on change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!formData.fullName || !formData.email || !formData.phone || !formData.password || !formData.identificationType) {
      setError('Please fill in all fields to create your account.');
      return;
    }

    setLoading(true);

    try {
      const nameParts = formData.fullName.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ') || '';

      const signupData = {
        firstName,
        lastName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        customerType: formData.role,
        identificationType: formData.identificationType
      };

      const response = await authService.signup(signupData);

      if (response.action === 1) {
        setPendingVerificationContext({
          customerId: response.customerId || response.id,
          role: formData.role
        });
        navigate('/verify', { state: { role: formData.role, customerId: response.customerId || response.id } });
      } else {
        setError(response.message || 'Registration failed.');
      }
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout registration>

            <div className="mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1F1F1F] mb-3">
                {formData.role === 'CAR_OWNER' ? 'Become Our Partner' : 'Create Your Account'}
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed">
                {formData.role === 'CAR_OWNER'
                  ? 'List your car, manage bookings, and connect with customers planning their next journey.'
                  : 'Create one account to rent cars, book drivers, and explore partnership opportunities.'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm font-medium">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-brand-black mb-2">Full Name</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="e.g. John Doe"
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-brand-orange-500 transition-all placeholder:text-gray-400 text-sm bg-white"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-black mb-2">Email Address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="e.g. john@example.com"
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-brand-orange-500 transition-all placeholder:text-gray-400 text-sm bg-white"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-black mb-2">Phone Number</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="e.g. +1 (555) 000-0000"
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-brand-orange-500 transition-all placeholder:text-gray-400 text-sm bg-white"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-black mb-2">Identification Type</label>
                <select
                  name="identificationType"
                  value={formData.identificationType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-brand-orange-500 transition-all text-sm bg-white"
                  disabled={loading}
                >
                  <option value="">Select identification type</option>
                  <option value="PASSPORT">Passport</option>
                  <option value="NATIONAL_ID">National ID</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-black mb-2">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-brand-orange-500 transition-all placeholder:text-gray-400 text-sm bg-white"
                    disabled={loading}
                  />
                  <button type="button" className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <Eye className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-[11px] text-gray-500 mt-2">Must be at least 8 characters long.</p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-orange-500 text-white py-3 mt-2 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-brand-orange-600 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                {loading ? 'Creating Account...' : 'Create Account'}
                {!loading && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                )}
              </button>

              <div className="relative flex items-center justify-center my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <span className="relative bg-[#faf8f5] px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">OR CONTINUE WITH</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <GoogleAuthButton
                  customerType={formData.role}
                  navigate={navigate}
                  onError={setError}
                  className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white px-2 py-2 shadow-sm"
                />
                <button type="button" className="w-full bg-white border border-gray-200 text-brand-black py-3 rounded-xl font-bold flex items-center justify-center gap-2 text-sm hover:bg-gray-50 transition-colors shadow-sm">
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
                    <path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z" />
                  </svg>
                  Apple
                </button>
              </div>
            </form>

            <div className="mt-8 text-center px-4">
              <p className="text-[11px] text-gray-500 leading-relaxed">
                By creating an account, you agree to our <Link to="/terms-of-service" className="underline hover:text-gray-900">Terms of Service</Link> and <Link to="/privacy-policy" className="underline hover:text-gray-900">Privacy Policy</Link>.

              </p>
            </div>
    </AuthLayout>
  );
};

export default Register;
